import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { subscribeNewsletter } from '../lib/supabase';

function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    await subscribeNewsletter(email);
    setStatus('done');
    setEmail('');
  };

  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 40, marginBottom: 40, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'white', marginBottom: 4 }}>Stay in Bloom 🌸</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>New designs, offers and seasonal collections — straight to your inbox.</div>
      </div>
      {status === 'done' ? (
        <div style={{ fontSize: 13, color: 'var(--rose)', fontWeight: 500 }}>Thanks for subscribing! 💗</div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="email" required value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Your email address"
            style={{ padding: '10px 16px', borderRadius: 40, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'white', fontSize: 13, minWidth: 220, outline: 'none' }}
          />
          <button type="submit" disabled={status === 'sending'} className="btn-primary" style={{ padding: '10px 24px', fontSize: 13, opacity: status === 'sending' ? 0.7 : 1 }}>
            {status === 'sending' ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
      )}
    </div>
  );
}

function TrustBadgeStrip() {
  const badges = [
    { icon: '🔒', label: 'Secure Payments', desc: 'Razorpay-protected checkout' },
    { icon: '♾️', label: 'Handmade Guarantee', desc: 'Never wilts, ever' },
    { icon: '📦', label: 'Pan-India Shipping', desc: 'Live courier rates at checkout' },
    { icon: '↩️', label: 'Easy Resolution', desc: 'Damaged on arrival? We fix it' },
  ];
  return (
    <div style={{ background: 'var(--bg-tertiary)', borderTop: '1px solid var(--border-color)' }}>
      <div className="container" style={{ padding: '28px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
        {badges.map(b => (
          <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>{b.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{b.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Footer() {
  const { isDark } = useTheme();

  return (
    <>
    <TrustBadgeStrip />
    <footer style={{
      background: isDark ? '#0e0a0c' : '#2a1a22',
      color: 'rgba(255,255,255,0.65)',
      marginTop: 'auto',
      transition: 'background 0.3s ease',
    }}>
      <div className="container" style={{ padding: '64px 24px 32px' }}>
        <NewsletterSignup />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 48, marginBottom: 48 }}>

          {/* Brand with real logo */}
          <div>
            <Link to="/" style={{ display: 'inline-block', marginBottom: 16 }}>
              <img
                src="/logo-dark.png"
                alt="Bloomwish"
                style={{ height: 48, width: 'auto', objectFit: 'contain' }}
              />
            </Link>
            <p style={{ fontSize: 13, lineHeight: 1.8, maxWidth: 240, color: 'rgba(255,255,255,0.55)' }}>
              Handcrafted pipe cleaner flowers made with love in Nagpur, Maharashtra. Each bloom is unique and lasts forever.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {[
                { label: 'Instagram', href: '#' },
                { label: 'Pinterest', href: '#' },
                { label: 'WhatsApp', href: 'https://wa.me/918668324650' },
              ].map(s => (
                <a key={s.label} href={s.href} target={s.href !== '#' ? '_blank' : undefined} rel={s.href !== '#' ? 'noopener noreferrer' : undefined} style={{
                  background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)',
                  padding: '7px 14px', borderRadius: 20, fontSize: 12,
                  transition: 'background 0.2s',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,122,148,0.3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                >{s.label}</a>
              ))}
            </div>
          </div>

          {/* Shop */}
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'white', marginBottom: 16 }}>Shop</div>
            {[
              { label: 'Single Flowers', slug: 'single-flowers' },
              { label: 'Bouquets', slug: 'bouquets' },
              { label: 'Flower Pots', slug: 'flower-pots' },
              { label: 'Flower Baskets', slug: 'flower-baskets' },
              { label: 'Butterfly Bouquets', slug: 'butterfly-bouquets' },
              { label: 'Custom Bouquets', slug: 'custom-bouquets' },
              { label: 'Handmade Decor & Gifts', slug: 'handmade-decor' },
              { label: 'Keychains', slug: 'keychains' },
              { label: 'Trendy Picks', slug: 'trendy-picks' },
              { label: 'Craft Materials', slug: 'craft-materials' },
            ].map(cat => (
              <Link key={cat.slug} to={`/shop/${cat.slug}`}
                style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 9, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--rose)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}
              >{cat.label}</Link>
            ))}
          </div>

          {/* Help */}
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'white', marginBottom: 16 }}>Help</div>
            {[
              { label: 'Custom Order', to: '/custom-order' },
              { label: 'Track My Order', to: '/track-order' },
              { label: 'FAQ', to: '/faq' },
              { label: 'About Us', to: '/about' },
              { label: 'Contact Us', to: '/contact' },
            ].map(l => (
              <Link key={l.to} to={l.to}
                style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 9, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--rose)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}
              >{l.label}</Link>
            ))}
          </div>

          {/* Contact */}
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'white', marginBottom: 16 }}>Get in Touch</div>
            <p style={{ fontSize: 13, marginBottom: 8 }}>📍 Nagpur, Maharashtra, India</p>
            <p style={{ fontSize: 13, marginBottom: 8 }}>📧 hello@bloomwish.in</p>
            <a href="https://wa.me/918668324650" target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: 13, marginBottom: 20, color: 'inherit', textDecoration: 'none' }}>📱 WhatsApp: +91 86683 24650</a>
            <Link to="/custom-order" style={{
              display: 'inline-block', background: 'var(--rose)', color: 'white',
              padding: '10px 24px', borderRadius: 40, fontSize: 13, fontWeight: 500,
              transition: 'background 0.2s',
            }}>Custom Order →</Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
            © {new Date().getFullYear()} Bloomwish. All rights reserved. Made with 💗 in Nagpur.
          </p>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Privacy Policy', 'Terms of Service', 'Refund Policy'].map(p => (
              <span key={p} style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.6)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.3)'}
              >{p}</span>
            ))}
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
